import { useMemo, type ReactNode } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
  AccordionItem,
  Button,
} from 'src/shared/components/ui'
import { Badge } from 'src/shared/components/ui/badge'
import { Check, Trash2, X } from 'lucide-react'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { formatDateTime } from 'src/shared/utils'
import {
  getFilenameFromUrl,
  type ProofEntry,
  type ProofStatus,
} from 'src/shared/utils/proofUrls'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { ProofCommentField } from './ProofCommentField'
import { ProofMediaPreview, ProofReviewWorkspace, ProofThumbnail } from './ProofReviewWorkspace'
import { statusBadgeVariant, statusLabel } from './proofListUtils'

const ProofListHeader = ({
  url,
  status,
  created_at,
  showStatus,
  locale,
  t,
  className = 'px-4 py-3',
}: {
  url: string
  status: ProofStatus
  created_at?: string
  showStatus: boolean
  locale: string
  t: (en: string, sv?: string) => string
  className?: string
}) => {
  const filename = getFilenameFromUrl(url)

  return (
    <Row className={`w-full items-center gap-3 flex-wrap ${className}`}>
      <ProofThumbnail url={url} filename={filename} className="h-10 w-10" />

      <Column className="min-w-0 flex-1 items-start gap-0.5 text-left">
        <span
          className="max-w-[12rem] truncate text-sm font-medium text-foreground"
          title={filename}
        >
          {filename}
        </span>
        {created_at ? (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(created_at, locale)}
          </span>
        ) : null}
      </Column>

      {showStatus ? (
        <Badge variant={statusBadgeVariant(status)}>{statusLabel(status, t)}</Badge>
      ) : null}
    </Row>
  )
}

const ProofListBody = ({
  proof,
  index,
  url,
  status,
  readOnly,
  canRemove,
  canSetStatus,
  canComment,
  onRemoveClick,
  onStatusChange,
  onCommentChange,
  t,
}: {
  proof: ProofEntry
  index: number
  url: string
  status: ProofStatus
  readOnly: boolean
  canRemove: boolean
  canSetStatus: boolean
  canComment: boolean
  onRemoveClick?: (url: string) => void
  onStatusChange?: (url: string, status: ProofStatus) => void
  onCommentChange?: (proofId: string, comment: string) => void
  t: (en: string, sv?: string) => string
}) => {
  const filename = getFilenameFromUrl(url)
  const showApproveDeny = canSetStatus && status === 'pending'
  const canEditComment =
    canComment && Boolean(proof.proof_id) && !readOnly && status === 'pending'
  const hasComment = Boolean(proof.customer_comment?.trim())

  return (
    <Column className="gap-4">
      {(showApproveDeny || canRemove) && (
        <Row className="flex-wrap items-center px-4">
          {canRemove && onRemoveClick && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRemoveClick(url)}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('Remove from order', 'Ta bort från ordern')}
            </Button>
          )}

          {showApproveDeny && onStatusChange && (
            <Row className="ml-auto items-center gap-2">
              <Button size="sm" onClick={() => onStatusChange(url, 'approved')}>
                <Check className="mr-1 h-3.5 w-3.5 text-green-500" />
                {t('Approve', 'Godkänn')}
              </Button>

              <Button
                size="sm"
                variant="secondary"
                onClick={() => onStatusChange(url, 'not_approved')}
              >
                <X className="mr-1 h-3.5 w-3.5 text-red-500" />
                {t('Deny', 'Neka')}
              </Button>
            </Row>
          )}
        </Row>
      )}

      {canEditComment || hasComment ? (
        <div className="px-4">
          <ProofCommentField
            proofId={proof.proof_id ?? `proof-${index}`}
            value={proof.customer_comment ?? ''}
            readOnly={!canEditComment}
            onSave={(comment) => onCommentChange?.(proof.proof_id!, comment)}
          />
        </div>
      ) : null}

      <ProofMediaPreview
        url={url}
        filename={filename}
        className="bg-gray-50"
        iframeClassName="h-[min(80vh,900px)] w-full bg-gray-50"
        imageClassName="max-h-[min(80vh,900px)] w-full object-contain bg-gray-50"
      />
    </Column>
  )
}

export const ProofList = ({
  proofs,
  readOnly = false,
  isShareOrder,
  quoteId,
  onRemoveClick,
  onStatusChange,
  onCommentChange,
  preserveOrder = false,
  getRowKey,
  showStatus = true,
  layout = 'review',
  toolbar,
  embedded = false,
}: {
  proofs: ProofEntry[]
  readOnly?: boolean
  isShareOrder?: boolean
  quoteId?: number
  onRemoveClick?: (url: string) => void
  onStatusChange?: (url: string, status: ProofStatus) => void
  onCommentChange?: (proofId: string, comment: string) => void
  preserveOrder?: boolean
  getRowKey?: (proof: ProofEntry, index: number) => string
  showStatus?: boolean
  layout?: 'review' | 'accordion' | 'expanded'
  toolbar?: ReactNode
  embedded?: boolean
}) => {
  const { t, locale } = useLocalization()
  const isAdmin = useIsAdmin()
  const canRemove = Boolean(
    isAdmin && !readOnly && !isShareOrder && quoteId && onRemoveClick
  )
  const canSetStatus = !readOnly && onStatusChange != null
  const canComment = onCommentChange != null

  const orderedProofs = useMemo(
    () =>
      preserveOrder
        ? proofs ?? []
        : [...(proofs ?? [])].sort(
            (a, b) =>
              new Date(b.created_at || '').getTime() -
              new Date(a.created_at || '').getTime()
          ),
    [proofs, preserveOrder]
  )

  if (layout === 'review') {
    return (
      <ProofReviewWorkspace
        proofs={orderedProofs}
        readOnly={readOnly}
        isShareOrder={isShareOrder}
        quoteId={quoteId}
        onRemoveClick={onRemoveClick}
        onStatusChange={onStatusChange}
        onCommentChange={onCommentChange}
        showStatus={showStatus}
        toolbar={toolbar}
        embedded={embedded}
        t={t}
        locale={locale}
      />
    )
  }

  if (layout === 'expanded') {
    return (
      <Column className="divide-y divide-gray-200">
        {orderedProofs.map((proof, index) => {
          const { url, status, created_at } = proof
          const rowKey = getRowKey?.(proof, index) ?? `${url}-${index}`

          return (
            <Column key={rowKey} className="gap-4 pb-6 pt-2">
              <ProofListHeader
                url={url}
                status={status}
                created_at={created_at}
                showStatus={showStatus}
                locale={locale}
                t={t}
              />
              <ProofListBody
                proof={proof}
                index={index}
                url={url}
                status={status}
                readOnly={readOnly}
                canRemove={canRemove}
                canSetStatus={canSetStatus}
                canComment={canComment}
                onRemoveClick={onRemoveClick}
                onStatusChange={onStatusChange}
                onCommentChange={onCommentChange}
                t={t}
              />
            </Column>
          )
        })}
      </Column>
    )
  }

  return (
    <Accordion type="single" collapsible>
      {orderedProofs.map((proof, index) => {
        const { url, status, created_at } = proof
        const value = getRowKey?.(proof, index) ?? `proof-${index}`
        const rowKey = getRowKey?.(proof, index) ?? `${url}-${index}`

        return (
          <AccordionItem
            key={rowKey}
            value={value}
            borderBottom={false}
            className="mb-2 rounded-md border border-gray-200 bg-white"
          >
            <AccordionTrigger className="px-4 py-2.5 shadow-sm hover:no-underline">
              <ProofListHeader
                url={url}
                status={status}
                created_at={created_at}
                showStatus={showStatus}
                locale={locale}
                t={t}
                className="px-0 py-0"
              />
            </AccordionTrigger>

            <AccordionContent className="pb-4">
              <ProofListBody
                proof={proof}
                index={index}
                url={url}
                status={status}
                readOnly={readOnly}
                canRemove={canRemove}
                canSetStatus={canSetStatus}
                canComment={canComment}
                onRemoveClick={onRemoveClick}
                onStatusChange={onStatusChange}
                onCommentChange={onCommentChange}
                t={t}
              />
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}
